import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './CoolerStuff.css';

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface IpLocation {
  city?: string; region?: string; country_name?: string;
  latitude?: number; longitude?: number; org?: string;
  timezone?: string; error?: boolean; reason?: string;
}

interface WeatherData {
  current?: {
    temperature_2m: number; relative_humidity_2m: number;
    weather_code: number; wind_speed_10m: number;
  };
}

interface AlgoliaHit { objectID: string; title: string; url?: string; }

interface EspnCompetitor {
  homeAway: string; team: { abbreviation: string; displayName: string };
  score?: string; winner?: boolean;
}
interface EspnGame {
  id: string;
  competitions: Array<{
    competitors: EspnCompetitor[];
    status: { type: { state: string; completed: boolean; shortDetail: string } };
  }>;
}

interface GolfCompetitor {
  id: string;
  athlete: { displayName: string; shortName?: string };
  score?: string;
  sortOrder?: number;
  status?: { thru?: string; period?: number; type?: { state?: string; shortDetail?: string } };
}

interface StockQuote {
  symbol: string; name: string; price: number;
  change: number; changePercent: number;
  group: 'tech' | 'energy' | 'futures';
}

// ─── Constants ────────────────────────────────────────────────────────────────

type SportTab = 'pga' | 'nba' | 'nhl' | 'mlb';

const ESPN_ENDPOINTS: Record<SportTab, string> = {
  pga: 'https://site.api.espn.com/apis/site/v2/sports/golf/pga/scoreboard',
  nba: 'https://site.api.espn.com/apis/site/v2/sports/basketball/nba/scoreboard',
  nhl: 'https://site.api.espn.com/apis/site/v2/sports/hockey/nhl/scoreboard',
  mlb: 'https://site.api.espn.com/apis/site/v2/sports/baseball/mlb/scoreboard',
};

const STOCK_SEEDS: Array<{ symbol: string; name: string; price: number; group: StockQuote['group'] }> = [
  { symbol: 'NVDA',  name: 'NVIDIA',      price: 124.50, group: 'tech' },
  { symbol: 'AAPL',  name: 'Apple',       price: 234.80, group: 'tech' },
  { symbol: 'MSFT',  name: 'Microsoft',   price: 411.20, group: 'tech' },
  { symbol: 'GOOGL', name: 'Alphabet',    price: 188.60, group: 'tech' },
  { symbol: 'META',  name: 'Meta',        price: 651.30, group: 'tech' },
  { symbol: 'XOM',   name: 'ExxonMobil',  price: 117.40, group: 'energy' },
  { symbol: 'CVX',   name: 'Chevron',     price: 159.80, group: 'energy' },
  { symbol: 'XLE',   name: 'Energy ETF',  price: 93.20,  group: 'energy' },
  { symbol: 'CL=F',  name: 'WTI Crude',   price: 72.80,  group: 'futures' },
  { symbol: 'NG=F',  name: 'Nat. Gas',    price: 3.94,   group: 'futures' },
];

// ─── Utility functions ────────────────────────────────────────────────────────

function generateStockData(prev?: StockQuote[]): StockQuote[] {
  return STOCK_SEEDS.map(seed => {
    const prevQ = prev?.find(s => s.symbol === seed.symbol);
    const base = prevQ?.price ?? seed.price;
    const vol = seed.group === 'futures' ? 0.005 : 0.003;
    const delta = (Math.random() - 0.49) * vol * base;
    const price = parseFloat(Math.max(base + delta, 0.01).toFixed(seed.symbol === 'NG=F' ? 3 : 2));
    const change = parseFloat((price - seed.price).toFixed(2));
    const changePercent = parseFloat(((change / seed.price) * 100).toFixed(2));
    return { ...seed, price, change, changePercent };
  });
}

function fmtPrice(price: number, symbol: string): string {
  return symbol === 'NG=F' ? price.toFixed(3) : price.toFixed(2);
}

function golfScoreColor(score: string | undefined): string {
  if (!score || score === 'E') return 'text-slate-200';
  return score.startsWith('-') ? 'text-emerald-400' : 'text-rose-400';
}

function isUSMarketOpen(): boolean {
  const now = new Date();
  const day = now.getUTCDay();
  if (day === 0 || day === 6) return false;
  const etMins = (now.getUTCHours() - 5) * 60 + now.getUTCMinutes();
  return etMins >= 570 && etMins < 960;
}

function getWeatherIcon(code: number) {
  if (code === 0) return WeatherIconSun;
  if (code <= 3) return WeatherIconCloud;
  if (code <= 67 || (code >= 80 && code <= 82)) return WeatherIconRain;
  if (code <= 86 || code >= 71) return WeatherIconSnow;
  return WeatherIconCloud;
}

function getWeatherTheme(code: number): { badge: string; glow: string; label: string } {
  if (code === 0) return { badge: 'from-amber-500/30 to-orange-500/15', glow: 'bg-amber-500/[0.08]', label: 'text-amber-400/80' };
  if (code <= 3)  return { badge: 'from-slate-500/25 to-slate-600/10', glow: 'bg-slate-500/5', label: 'text-slate-400/80' };
  if (code <= 67 || (code >= 80 && code <= 82)) return { badge: 'from-cyan-500/25 to-blue-500/10', glow: 'bg-cyan-500/[0.08]', label: 'text-cyan-400/80' };
  return { badge: 'from-blue-200/20 to-slate-400/10', glow: 'bg-blue-200/5', label: 'text-blue-200/70' };
}

function wmoLabel(code: number) {
  if (code === 0) return 'Clear sky';
  if (code <= 3)  return 'Partly cloudy';
  if (code <= 67) return 'Rainy';
  if (code <= 77) return 'Snowy';
  if (code <= 82) return 'Showers';
  if (code <= 86) return 'Heavy snow';
  return 'Mixed';
}

function cToF(c: number): number { return c * 9 / 5 + 32; }

function wmoDescription(code: number, temp: number) {
  const c = wmoLabel(code).toLowerCase();
  if (c === 'clear sky') return temp > 77 ? 'bright and warm' : temp > 59 ? 'clear and pleasant' : 'clear and crisp';
  if (c.includes('cloud')) return 'overcast and grey';
  if (c.includes('rain') || c.includes('shower')) return 'wet and rainy';
  if (c.includes('snow')) return 'cold and snowy';
  return 'mixed conditions';
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function IconArrowLeft() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>;
}
function IconSparkle() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.5 5h5l-4 3 1.5 5L12 13l-4 3 1.5-5-4-3h5L12 3z"/></svg>;
}
function IconMapPin() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>;
}
function IconBrain() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.46 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.46 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/></svg>;
}
function IconTrophy() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>;
}
function IconDroplet() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>;
}
function IconWind() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/></svg>;
}
function IconExternalLink() {
  return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>;
}
function IconMapView() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>;
}
function IconGrid() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>;
}
function IconGolfFlag() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>;
}
function IconTrendingUp() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>;
}
function IconArrowUpRight() {
  return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>;
}
function IconArrowDownRight() {
  return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="7" x2="17" y2="17"/><polyline points="17 7 17 17 7 17"/></svg>;
}

// ─── Weather icon components ──────────────────────────────────────────────────

function WeatherIconSun({ size = 44 }: { size?: number }) {
  return <svg className="text-amber-400" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>;
}
function WeatherIconCloud({ size = 44 }: { size?: number }) {
  return <svg className="text-slate-300" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>;
}
function WeatherIconRain({ size = 44 }: { size?: number }) {
  return <svg className="text-cyan-400" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/><path d="M8 16v2M12 18v2M16 16v2"/></svg>;
}
function WeatherIconSnow({ size = 44 }: { size?: number }) {
  return <svg className="text-blue-200" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/><path d="M12 14v2M11 13l1 2 1-2M12 16l-1 2 1 2M10 15l-2 1 2 1M14 15l2 1-2 1"/></svg>;
}

// ─── StockCard sub-component ──────────────────────────────────────────────────

function StockCard({ stock, flash }: { stock: StockQuote; flash?: 'up' | 'down' }) {
  const isUp = stock.changePercent >= 0;
  const displaySym = stock.symbol === 'CL=F' ? 'WTI' : stock.symbol === 'NG=F' ? 'NG' : stock.symbol;
  return (
    <div className={`rounded-xl border border-white/[0.07] bg-white/[0.025] p-3 transition-all hover:border-white/[0.13] hover:bg-white/[0.04] ${
      flash === 'up' ? 'price-flash-up' : flash === 'down' ? 'price-flash-down' : ''
    }`}>
      <div className="mb-1.5 flex items-start justify-between gap-1">
        <span className="text-sm font-bold tracking-wide text-white">{displaySym}</span>
        <span className={`flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-bold ${isUp ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
          {isUp ? <IconArrowUpRight /> : <IconArrowDownRight />}
          {Math.abs(stock.changePercent).toFixed(2)}%
        </span>
      </div>
      <div className="mb-2 text-[10px] text-slate-600">{stock.name}</div>
      <div className="text-lg font-bold tabular-nums text-white">${fmtPrice(stock.price, stock.symbol)}</div>
      <div className={`mt-0.5 text-[10px] tabular-nums ${isUp ? 'text-emerald-500' : 'text-rose-500'}`}>
        {isUp ? '+' : ''}{stock.change.toFixed(2)}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function CoolerStuffPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [ipLoc, setIpLoc] = useState<IpLocation | null>(null);
  const [ipError, setIpError] = useState<string | null>(null);
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [weatherError, setWeatherError] = useState<string | null>(null);
  const [news, setNews] = useState<AlgoliaHit[]>([]);
  const [newsError, setNewsError] = useState<string | null>(null);
  const [typedIndex, setTypedIndex] = useState(0);
  const [agentDone, setAgentDone] = useState(false);
  const [sportTab, setSportTab] = useState<SportTab>('pga');
  const [sports, setSports] = useState<EspnGame[]>([]);
  const [sportsError, setSportsError] = useState<string | null>(null);
  const [golfPlayers, setGolfPlayers] = useState<GolfCompetitor[]>([]);
  const [golfTournament, setGolfTournament] = useState('');
  const [golfStatus, setGolfStatus] = useState('');
  const [stocks, setStocks] = useState<StockQuote[]>(() => generateStockData());
  const [stockFlash, setStockFlash] = useState<Record<string, 'up' | 'down'>>({});
  const [stocksUpdated, setStocksUpdated] = useState(() => new Date());
  const [marketOpen, setMarketOpen] = useState(isUSMarketOpen);

  // IP geo
  useEffect(() => {
    let cancelled = false;
    fetch('https://ipapi.co/json/')
      .then(r => r.json()).then((d: IpLocation) => {
        if (cancelled) return;
        if (d.error) { setIpError(d.reason || 'Unavailable'); return; }
        if (d.latitude != null && d.longitude != null) setIpLoc(d);
        else setIpError('No coordinates');
      }).catch(() => { if (!cancelled) setIpError('Network error'); });
    return () => { cancelled = true; };
  }, []);

  const lat = ipLoc?.latitude ?? 40.71;
  const lon = ipLoc?.longitude ?? -74.01;

  // Weather
  useEffect(() => {
    let cancelled = false;
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m`)
      .then(r => r.json()).then((d: WeatherData) => {
        if (!cancelled && d.current) setWeather(d);
        else if (!cancelled) setWeatherError('No data');
      }).catch(() => { if (!cancelled) setWeatherError('Failed'); });
    return () => { cancelled = true; };
  }, [lat, lon]);

  // AI news
  useEffect(() => {
    let cancelled = false;
    fetch('https://hn.algolia.com/api/v1/search_by_date?query=AI+LLM+OpenAI+Claude+machine+learning&tags=story&hitsPerPage=5')
      .then(r => r.json()).then((d: { hits: AlgoliaHit[] }) => {
        if (cancelled) return;
        if (d.hits?.length) setNews(d.hits); else setNewsError('No stories');
      }).catch(() => { if (!cancelled) setNewsError('Failed'); });
    return () => { cancelled = true; };
  }, []);

  // Sports / Golf
  useEffect(() => {
    let cancelled = false;
    setSports([]); setSportsError(null); setGolfPlayers([]); setGolfTournament(''); setGolfStatus('');
    if (sportTab === 'pga') {
      fetch(ESPN_ENDPOINTS.pga)
        .then(r => r.json())
        .then((d: { events?: Array<{ name: string; shortName: string; competitions: Array<{ competitors: GolfCompetitor[]; status: { type: { state: string; shortDetail: string } } }> }> }) => {
          if (cancelled) return;
          const ev = d.events?.[0];
          if (!ev) { setSportsError('No tournament this week'); return; }
          setGolfTournament(ev.name || ev.shortName || 'PGA Tour');
          const comp = ev.competitions?.[0];
          if (!comp?.competitors?.length) { setSportsError('Leaderboard not yet available'); return; }
          setGolfStatus(comp.status?.type?.shortDetail || '');
          const sorted = [...comp.competitors].sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
          setGolfPlayers(sorted.slice(0, 12));
        })
        .catch(() => { if (!cancelled) setSportsError('Scores unavailable'); });
    } else {
      fetch(ESPN_ENDPOINTS[sportTab])
        .then(r => r.json()).then((d: { events?: EspnGame[] }) => {
          if (cancelled) return;
          if (d.events?.length) setSports(d.events.slice(0, 8));
          else setSportsError('No games today');
        }).catch(() => { if (!cancelled) setSportsError('Scores unavailable'); });
    }
    return () => { cancelled = true; };
  }, [sportTab]);

  // Stock ticker simulation — refreshes every 12s
  useEffect(() => {
    const iv = setInterval(() => {
      setStocks(prev => {
        const next = generateStockData(prev);
        const flashes: Record<string, 'up' | 'down'> = {};
        next.forEach(s => {
          const p = prev.find(x => x.symbol === s.symbol);
          if (p && s.price !== p.price) flashes[s.symbol] = s.price > p.price ? 'up' : 'down';
        });
        if (Object.keys(flashes).length) {
          setStockFlash(flashes);
          setTimeout(() => setStockFlash({}), 900);
        }
        setStocksUpdated(new Date());
        return next;
      });
    }, 12000);
    return () => clearInterval(iv);
  }, []);

  // Market open check
  useEffect(() => {
    const t = setInterval(() => setMarketOpen(isUSMarketOpen()), 60000);
    return () => clearInterval(t);
  }, []);

  // Agent message
  const agentMessage = useMemo(() => {
    const loc = ipLoc ? `${ipLoc.city}, ${ipLoc.region ? ipLoc.region + ', ' : ''}${ipLoc.country_name}` : 'an unknown node';
    const temp = weather?.current?.temperature_2m ?? null;
    const code = weather?.current?.weather_code ?? 0;
    const wind = weather?.current?.wind_speed_10m ?? 0;
    const tempF = temp != null ? cToF(temp) : null;
    const weatherDesc = tempF != null ? wmoDescription(code, tempF) : 'unknown';
    const windLine = wind > 20 ? ' Winds are up — stay grounded.' : wind > 10 ? ' A light breeze out there.' : '';
    const firstHeadline = news[0]?.title ? ` Latest AI signal: "${news[0].title.slice(0, 55)}${news[0].title.length > 55 ? '…' : ''}"` : '';
    return `Hello. You're connecting from ${loc}. Current conditions look ${weatherDesc}${tempF != null ? ` at ${Math.round(tempF)}°F.` : '.'}${windLine}${firstHeadline}`;
  }, [ipLoc, weather, news]);

  useEffect(() => { setTypedIndex(0); setAgentDone(false); }, [agentMessage]);
  useEffect(() => {
    if (typedIndex >= agentMessage.length) { setAgentDone(true); return; }
    const t = setTimeout(() => setTypedIndex(i => i + 1), 28 + Math.random() * 22);
    return () => clearTimeout(t);
  }, [typedIndex, agentMessage]);

  const mapLat = ipLoc?.latitude ?? 40;
  const mapLon = ipLoc?.longitude ?? -98;
  const mapBbox = `${mapLon - 2},${mapLat - 1.5},${mapLon + 2},${mapLat + 1.5}`;
  const osmEmbed = `https://www.openstreetmap.org/export/embed.html?bbox=${mapBbox}&layer=mapnik&marker=${mapLat},${mapLon}`;

  const weatherCode = weather?.current?.weather_code ?? 0;
  const WeatherIcon = getWeatherIcon(weatherCode);
  const weatherTheme = getWeatherTheme(weatherCode);

  const scoresLoaded = sportTab === 'pga' ? golfPlayers.length > 0 : sports.length > 0;

  const techStocks  = stocks.filter(s => s.group === 'tech');
  const energyStocks = stocks.filter(s => s.group === 'energy');
  const futuresStocks = stocks.filter(s => s.group === 'futures');

  return (
    <div className="cooler-page flex min-h-screen flex-col text-slate-100">

      {/* Ambient background blobs */}
      <div aria-hidden="true" className="cooler-blob-layer" style={{ zIndex: 0 }}>
        <div className="cooler-blob cooler-blob-violet" />
        <div className="cooler-blob cooler-blob-cyan" />
        <div className="cooler-blob cooler-blob-amber" />
      </div>

      {/* Header */}
      <header className="flex-shrink-0 border-b border-white/[0.06] bg-black/30 px-4 py-3 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" className="cooler-link flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-slate-200">
            <IconArrowLeft /><span>Home</span>
          </Link>
          <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-violet-600 to-cyan-500 text-white">
              <IconGrid />
            </div>
            <h1 className="text-gradient-violet-cyan text-sm font-semibold tracking-wide">Dashboard</h1>
          </div>
          <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 font-mono text-[10px] text-slate-500">v0.5</span>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-3 px-4 py-4">

        {/* Agent */}
        <section className="cooler-agent-card flex-shrink-0 p-4">
          <div className="flex items-start gap-3.5">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600/90 to-cyan-500/70 text-white shadow-lg shadow-violet-500/20">
              <IconSparkle />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-violet-400">Agent</span>
                <span className="flex h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.7)]" />
                <span className="text-[10px] text-slate-600">online</span>
              </div>
              <p className="text-sm leading-relaxed text-slate-200">
                {agentMessage.slice(0, typedIndex)}
                {!agentDone && <span className="cooler-cursor ml-0.5 inline-block h-4 w-0.5 bg-violet-400" />}
              </p>
            </div>
          </div>
        </section>

        {/* Top 3 panels */}
        <div className="grid flex-shrink-0 grid-cols-1 gap-3 sm:grid-cols-3">

          {/* Location */}
          <section className="cooler-card cooler-card-violet h-44 p-4">
            <div className="pointer-events-none absolute -right-4 -top-4 h-24 w-24 rounded-full bg-violet-500/10 blur-2xl" />
            <div className="relative flex h-full flex-col">
              <div className="mb-3 flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-500/20 text-violet-400"><IconMapPin /></div>
                <span className="text-[10px] font-semibold uppercase tracking-widest text-violet-400/80">Location</span>
              </div>
              {ipError && <p className="text-sm text-slate-400">{ipError}</p>}
              {!ipError && !ipLoc && (
                <div className="flex-1 space-y-2">
                  <div className="cooler-shimmer h-5 w-3/4 rounded-md" />
                  <div className="cooler-shimmer h-3.5 w-1/2 rounded-md" />
                </div>
              )}
              {ipLoc && (
                <>
                  <div className="flex-1">
                    <div className="text-lg font-bold leading-tight text-white">{ipLoc.city || 'Unknown'}</div>
                    <div className="mt-0.5 text-sm text-slate-400">{[ipLoc.region, ipLoc.country_name].filter(Boolean).join(', ')}</div>
                  </div>
                  <div className="space-y-0.5">
                    <div className="font-mono text-[10px] text-slate-600">{ipLoc.latitude?.toFixed(4)}, {ipLoc.longitude?.toFixed(4)}</div>
                    {ipLoc.org && <div className="truncate text-[10px] text-slate-700">{ipLoc.org}</div>}
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Weather */}
          <section className="cooler-card cooler-card-amber h-44 p-4">
            <div className={`pointer-events-none absolute -right-4 -top-4 h-28 w-28 rounded-full blur-2xl ${weatherTheme.glow}`} />
            <div className="relative flex h-full flex-col">
              <div className="mb-2 flex items-center justify-between">
                <span className={`text-[10px] font-semibold uppercase tracking-widest ${weatherTheme.label}`}>Weather</span>
                {ipLoc?.city && <span className="text-[10px] text-slate-600">{ipLoc.city}</span>}
              </div>
              {weatherError && <p className="text-sm text-slate-400">{weatherError}</p>}
              {!weatherError && !weather && (
                <div className="flex flex-1 items-center gap-3">
                  <div className={`cooler-shimmer h-14 w-14 flex-shrink-0 rounded-2xl bg-gradient-to-br ${weatherTheme.badge}`} />
                  <div className="space-y-2"><div className="cooler-shimmer h-8 w-16 rounded-md" /><div className="cooler-shimmer h-3 w-12 rounded-md" /></div>
                </div>
              )}
              {weather?.current && (
                <>
                  <div className="flex flex-1 items-center gap-3">
                    <div className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${weatherTheme.badge}`}>
                      <WeatherIcon size={36} />
                    </div>
                    <div>
                      <div className="text-4xl font-bold leading-none text-white">{Math.round(cToF(weather.current.temperature_2m))}°F</div>
                      <div className="mt-1 text-xs text-slate-400">{wmoLabel(weatherCode)}</div>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-1 text-slate-500"><IconDroplet /><span className="text-xs">{weather.current.relative_humidity_2m}%</span></div>
                    <div className="flex items-center gap-1 text-slate-500"><IconWind /><span className="text-xs">{weather.current.wind_speed_10m} km/h</span></div>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* AI News */}
          <section className="cooler-card cooler-card-cyan h-44 p-4">
            <div className="pointer-events-none absolute -right-4 -top-4 h-24 w-24 rounded-full bg-cyan-500/[0.08] blur-2xl" />
            <div className="relative flex h-full flex-col">
              <div className="mb-3 flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-cyan-500/20 text-cyan-400"><IconBrain /></div>
                <span className="text-[10px] font-semibold uppercase tracking-widest text-cyan-400/80">AI Intel</span>
                <span className="ml-auto text-[9px] text-slate-700">via HN</span>
              </div>
              {newsError && <p className="text-sm text-slate-400">{newsError}</p>}
              {!newsError && news.length === 0 && (
                <div className="flex-1 space-y-2">
                  {[0.8, 0.6, 0.7].map((w, i) => <div key={i} className="cooler-shimmer h-3.5 rounded-md" style={{ width: `${w * 100}%` }} />)}
                </div>
              )}
              {news.length > 0 && (
                <ul className="flex-1 space-y-2 overflow-hidden">
                  {news.slice(0, 3).map(item => (
                    <li key={item.objectID}>
                      <a href={item.url || `https://news.ycombinator.com/item?id=${item.objectID}`} target="_blank" rel="noreferrer"
                        className="group flex items-start gap-2 transition-colors hover:text-cyan-300">
                        <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-cyan-500/50 transition-colors group-hover:bg-cyan-400" />
                        <span className="line-clamp-2 text-xs leading-snug text-slate-300 group-hover:text-cyan-300">{item.title}</span>
                        <span className="mt-0.5 flex-shrink-0 text-slate-700 transition-colors group-hover:text-cyan-500"><IconExternalLink /></span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>

        {/* Map + Status */}
        <div className="grid flex-shrink-0 grid-cols-1 gap-3 sm:grid-cols-4">
          <section className="cooler-map-section sm:col-span-3">
            <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-2.5">
              <div className="flex items-center gap-2 text-slate-400"><IconMapView /><span className="text-xs font-medium">Live location</span></div>
              {ipLoc && <span className="font-mono text-[10px] text-slate-600">{ipLoc.latitude?.toFixed(3)}, {ipLoc.longitude?.toFixed(3)}</span>}
            </div>
            <div className="relative h-44 w-full bg-slate-900">
              <iframe title="Location map" src={osmEmbed} className="h-full w-full border-0" loading="lazy" />
            </div>
          </section>
          <section className="cooler-card h-44 p-4">
            <div className="mb-3 flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/5 text-slate-400"><IconGrid /></div>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Services</span>
            </div>
            <ul className="space-y-2">
              {[
                { label: 'Geo',     ok: !!ipLoc,           loading: !ipLoc && !ipError },
                { label: 'Weather', ok: !!weather,          loading: !weather && !weatherError },
                { label: 'AI Feed', ok: news.length > 0,   loading: news.length === 0 && !newsError },
                { label: 'Scores',  ok: scoresLoaded,      loading: !scoresLoaded && !sportsError },
              ].map(({ label, ok, loading }) => (
                <li key={label} className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">{label}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${ok ? 'status-pill-ok' : 'status-pill-loading'}`}>
                    {ok ? 'OK' : loading ? '…' : 'ERR'}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        {/* Scoreboard */}
        <section className="cooler-card cooler-card-amber flex-shrink-0 p-5">
          <div className="pointer-events-none absolute -right-8 -top-8 h-48 w-48 rounded-full bg-amber-500/[0.05] blur-3xl" />
          <div className="relative">
            {/* Scoreboard header */}
            <div className="mb-5 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/20 text-amber-400">
                  {sportTab === 'pga' ? <IconGolfFlag /> : <IconTrophy />}
                </div>
                <span className="text-base font-bold text-white">Scoreboard</span>
                <span className="text-[10px] text-slate-600">ESPN</span>
              </div>
              <div className="flex gap-1.5">
                {(['pga', 'nba', 'nhl', 'mlb'] as SportTab[]).map(tab => (
                  <button key={tab} onClick={() => setSportTab(tab)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all ${
                      sportTab === tab
                        ? 'sport-tab-active border-amber-500/40 bg-amber-500/15 text-amber-400'
                        : 'border-white/[0.06] bg-white/[0.03] text-slate-600 hover:border-white/10 hover:text-slate-400'
                    }`}>
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* PGA Leaderboard */}
            {sportTab === 'pga' && (
              <div>
                {golfTournament && (
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-base font-bold text-white">{golfTournament}</span>
                    {golfStatus && (
                      <span className="rounded-full border border-amber-500/25 bg-amber-500/10 px-3 py-0.5 text-xs font-medium text-amber-400">
                        {golfStatus}
                      </span>
                    )}
                  </div>
                )}
                {sportsError && <p className="text-sm text-slate-500">{sportsError}</p>}
                {!golfTournament && !sportsError && (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {[...Array(8)].map((_, i) => <div key={i} className="cooler-shimmer h-12 rounded-xl border border-white/5" />)}
                  </div>
                )}
                {golfPlayers.length > 0 && (
                  <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                    {golfPlayers.map((player, idx) => (
                      <div key={player.id}
                        className="flex items-center gap-4 rounded-xl border border-white/[0.05] bg-white/[0.02] px-4 py-3 transition-colors hover:bg-white/[0.04]">
                        <span className="w-6 flex-shrink-0 text-right font-mono text-sm font-bold text-slate-700">{idx + 1}</span>
                        <span className="flex-1 truncate text-sm font-semibold text-slate-200">
                          {player.athlete.shortName || player.athlete.displayName}
                        </span>
                        <span className={`font-mono text-xl font-bold tabular-nums ${golfScoreColor(player.score)}`}>
                          {player.score || 'E'}
                        </span>
                        <span className="w-8 flex-shrink-0 text-right font-mono text-xs text-slate-600">
                          {player.status?.thru || player.status?.type?.shortDetail || '-'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Game sports */}
            {sportTab !== 'pga' && (
              <div>
                {sportsError && <p className="text-sm text-slate-500">{sportsError}</p>}
                {!sportsError && sports.length === 0 && (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {[...Array(4)].map((_, i) => <div key={i} className="cooler-shimmer h-24 rounded-xl border border-white/5" />)}
                  </div>
                )}
                {sports.length > 0 && (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {sports.map(event => {
                      const comp = event.competitions[0];
                      if (!comp) return null;
                      const away = comp.competitors.find(c => c.homeAway === 'away');
                      const home = comp.competitors.find(c => c.homeAway === 'home');
                      const state = comp.status?.type?.state;
                      const detail = comp.status?.type?.shortDetail || '';
                      const isLive = state === 'in';
                      const isFinal = state === 'post';
                      return (
                        <div key={event.id} className={`rounded-xl border p-3 transition-all ${
                          isLive ? 'border-emerald-500/30 bg-emerald-500/[0.05] shadow-[0_0_16px_rgba(52,211,153,0.08)]'
                                 : 'border-white/[0.06] bg-white/[0.02] hover:border-white/10'}`}>
                          <div className="mb-3 flex items-center justify-between">
                            {isLive ? (
                              <div className="flex items-center gap-1.5">
                                <span className="cooler-live-dot inline-block h-2 w-2 rounded-full bg-emerald-400" />
                                <span className="text-[9px] font-bold tracking-widest text-emerald-400">LIVE</span>
                              </div>
                            ) : isFinal ? (
                              <span className="text-xs font-semibold tracking-widest text-slate-600">FINAL</span>
                            ) : (
                              <span className="text-xs font-semibold tracking-widest text-slate-700">SCHED</span>
                            )}
                            <span className="font-mono text-[10px] text-slate-700">{detail}</span>
                          </div>
                          <div className="space-y-2">
                            {[away, home].filter((t): t is EspnCompetitor => !!t).map((team, i) => (
                              <div key={i} className="flex items-center justify-between">
                                <span className={`text-sm font-bold tracking-wide ${team.winner ? 'text-white' : 'text-slate-500'}`}>
                                  {team.team.abbreviation}
                                </span>
                                <span className={`font-mono text-xl font-bold tabular-nums ${
                                  isLive ? 'text-amber-400'
                                         : isFinal ? (team.winner ? 'text-white' : 'text-slate-600') : 'text-slate-700'}`}>
                                  {team.score ?? '–'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Markets */}
        <section className="cooler-card cooler-card-emerald flex-shrink-0 p-5">
          <div className="pointer-events-none absolute -left-8 -top-8 h-48 w-48 rounded-full bg-emerald-500/[0.05] blur-3xl" />
          <div className="relative">
            {/* Markets header */}
            <div className="mb-5 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/20 text-emerald-400">
                  <IconTrendingUp />
                </div>
                <span className="text-base font-bold text-white">Markets</span>
                <div className="flex items-center gap-1.5">
                  <span className={`inline-block h-2 w-2 rounded-full ${marketOpen ? 'market-open-dot bg-emerald-400' : 'bg-slate-600'}`} />
                  <span className={`text-[10px] font-semibold ${marketOpen ? 'text-emerald-400' : 'text-slate-600'}`}>
                    {marketOpen ? 'OPEN' : 'CLOSED'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px] text-slate-700">
                  {stocksUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
                <span className="rounded border border-slate-800 bg-slate-900/60 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-slate-700">simulated</span>
              </div>
            </div>

            {/* Tech stocks */}
            <div className="mb-4">
              <div className="mb-2.5 flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-violet-400/70">Tech</span>
                <div className="h-px flex-1 bg-gradient-to-r from-violet-500/20 to-transparent" />
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                {techStocks.map(s => <StockCard key={s.symbol} stock={s} flash={stockFlash[s.symbol]} />)}
              </div>
            </div>

            {/* Energy + Futures */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <div className="mb-2.5 flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-amber-400/70">Energy</span>
                  <div className="h-px flex-1 bg-gradient-to-r from-amber-500/20 to-transparent" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {energyStocks.map(s => <StockCard key={s.symbol} stock={s} flash={stockFlash[s.symbol]} />)}
                </div>
              </div>
              <div>
                <div className="mb-2.5 flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-orange-400/70">Futures</span>
                  <div className="h-px flex-1 bg-gradient-to-r from-orange-500/20 to-transparent" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {futuresStocks.map(s => <StockCard key={s.symbol} stock={s} flash={stockFlash[s.symbol]} />)}
                </div>
              </div>
            </div>

            {/* Ticker tape */}
            <div className="ticker-wrapper mt-4 border-t border-white/[0.04] pt-3">
              <div className="ticker-track">
                {[...stocks, ...stocks].map((s, i) => (
                  <span key={i} className={`font-mono text-[10px] font-semibold ${s.changePercent >= 0 ? 'text-emerald-500/70' : 'text-rose-500/70'}`}>
                    {s.symbol === 'CL=F' ? 'WTI' : s.symbol === 'NG=F' ? 'NG' : s.symbol}
                    {' '}
                    <span className="text-slate-400">${fmtPrice(s.price, s.symbol)}</span>
                    {' '}
                    {s.changePercent >= 0 ? '+' : ''}{s.changePercent.toFixed(2)}%
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

      </main>
    </div>
  );
}

export default CoolerStuffPage;
